import React from 'react';
import { Alert, Button } from '@shared/components';
import type { ElemisRefusal } from '@features/agreements';
import { checkoutOutcomeAlertText } from '../../utils/elemisCheckoutStatusText';

interface ElemisCheckoutRefusalAlertProps {
    refusal: ElemisRefusal | null;
    onRetry: () => void;
    /** A terminal frame event that is not a success; shown when no refusal is
     *  pending. Its Retry starts a brand-new checkout. */
    outcome?: 'failed' | 'expired' | 'cancelled' | null;
    isRetrying?: boolean;
    onRetryOutcome?: () => void;
}

/** The ONE `role="alert"` region of the checkout panel: a server refusal takes
 *  the region when present, otherwise a terminal frame outcome does. */
export const ElemisCheckoutRefusalAlert: React.FC<ElemisCheckoutRefusalAlertProps> = ({
    refusal,
    onRetry,
    outcome = null,
    isRetrying = false,
    onRetryOutcome,
}) => (
    <div role="alert" className="text-sm">
        {refusal && (
            <Alert variant="error" role="note">
                <div>
                    <p className="font-medium">{refusal.title}</p>
                    <p className="mt-1">{refusal.detail}</p>
                    {refusal.canRetry && (
                        <Button
                            size="sm"
                            variant="outline"
                            className="mt-3"
                            // A refusal on top of a terminal outcome must start a new
                            // checkout; plain initiation would reopen the dead one.
                            onClick={outcome && onRetryOutcome ? onRetryOutcome : onRetry}
                            disabled={isRetrying}
                        >
                            {isRetrying ? 'Starting payment…' : 'Try again'}
                        </Button>
                    )}
                </div>
            </Alert>
        )}
        {!refusal && outcome && (
            <Alert variant="error" role="note">
                <div>
                    <p className="font-medium">{checkoutOutcomeAlertText(outcome).title}</p>
                    <p className="mt-1">{checkoutOutcomeAlertText(outcome).detail}</p>
                    <Button size="sm" className="mt-3" onClick={onRetryOutcome} disabled={isRetrying}>
                        {isRetrying ? 'Starting payment…' : 'Retry payment'}
                    </Button>
                </div>
            </Alert>
        )}
    </div>
);
